// src/components/layout/MainLayout.tsx
import React, { useState } from 'react';
import styled from 'styled-components';
import { Outlet } from 'react-router-dom';
import Sidebar from './Sidebar';
import { Menu, X } from 'lucide-react';

const LayoutContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
`;

const MobileHeader = styled.div`
  display: none;
  background-color: ${({ theme }) => theme.colours.background.dark};
  padding: 1rem;
  align-items: center;
  justify-content: space-between;
  color: ${({ theme }) => theme.colours.text.inverse};
  
  @media (max-width: 768px) {
    display: flex;
  }
`;

const MobileMenuButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colours.text.inverse};
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

const MobileLogo = styled.div`
  font-size: 1.25rem;
  font-weight: 600;
`;

const MainContent = styled.div`
  display: flex;
  flex: 1;
  position: relative;
  overflow: auto;
`;

const SidebarContainer = styled.div<{ isOpen: boolean }>`
  width: 270px;
  background-color: ${({ theme }) => theme.colours.background.paper};
  box-shadow: ${({ theme }) => theme.shadows.small};
  transition: transform 0.3s ease;
  
  @media (max-width: 768px) {
    position: fixed;
    top: 0;
    left: 0;
    height: 100vh;
    z-index: 1000;
    transform: translateX(${({ isOpen }) => isOpen ? '0' : '-100%'});
  }
`;

const MobileOverlay = styled.div<{ isOpen: boolean }>`
  display: none;
  
  @media (max-width: 768px) {
    display: ${({ isOpen }) => isOpen ? 'block' : 'none'};
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 999;
  }
`;

const ContentContainer = styled.div`
  flex: 1;
  padding: ${({ theme }) => theme.spacing(3)};
  overflow-y: auto;
  background-color: ${({ theme }) => theme.colours.background.paper};
  
  @media (max-width: 768px) {
    padding: ${({ theme }) => theme.spacing(2)};
    margin-top: 0;
  }
  
  @media (max-width: 480px) {
    padding: ${({ theme }) => theme.spacing(1)};
  }
`;

const MainLayout: React.FC = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  
  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };
  
  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };
  
  return (
    <LayoutContainer>
      <MobileHeader>
        <MobileMenuButton onClick={toggleSidebar}>
          {isSidebarOpen ? <X size={24} /> : <Menu size={24} />}
        </MobileMenuButton>
        <MobileLogo>Oilwell Asset</MobileLogo>
        <div style={{ width: '40px' }} /> {/* Spacer for centering */}
      </MobileHeader>
      
      <MainContent>
        <MobileOverlay isOpen={isSidebarOpen} onClick={closeSidebar} />
        <SidebarContainer isOpen={isSidebarOpen}>
          <Sidebar onNavigate={closeSidebar} />
        </SidebarContainer>
        <ContentContainer>
          <Outlet />
        </ContentContainer>
      </MainContent>
    </LayoutContainer>
  );
};

export default MainLayout;